import { LOGIN_TYPES } from './types';
import StorageManager from '../../utils/StorageManager';

// Make sure the initial state is properly initialized from localStorage with error handling
const getInitialUserData = () => {
  return StorageManager.get('burrowUserData', null);
};

const initialState = {
  isAuth: Boolean(StorageManager.get('burrowtoken')),
  token: StorageManager.get('burrowtoken', ''),
  email: "",
  password: "",
  loginAttempts: 0,
  isAccountLocked: false,
  lockUntil: null,
  user: getInitialUserData(),
  isAdmin: StorageManager.get('isAdmin', false), // Initialize isAdmin from localStorage
  adminLevel: StorageManager.get('adminLevel', null), // owner | senior-admin | admin | moderator | null
  // Phase 1C: Sidecar admin mode state
  hasAdminAccess: StorageManager.get('hasAdminAccess', false), // User CAN elevate (has linked Admin doc)
  isInAdminMode: false, // User IS currently elevated (admin JWT active)
  adminToken: null, // Admin JWT (separate from user JWT in state.token)
  // New profile state
  profileLoading: false,
  profileError: null,
  // Initialize profile data from localStorage if available
  profile: {
    userBio: getInitialUserData()?.userBio || "",
    accountCredits: getInitialUserData()?.accountCredits || 0,
    burrowPoints: getInitialUserData()?.burrowPoints || 5,
    accountLevel: getInitialUserData()?.accountLevel || 1,
    accountType: getInitialUserData()?.accountType || "free",
    userAvatar: getInitialUserData()?.userAvatar || "", // Initialize userAvatar
    // Payment/membership defaults
    membershipTier: getInitialUserData()?.membershipTier || "base",
    subscriptionStatus: getInitialUserData()?.subscriptionStatus || "none",
    contributionAmount: getInitialUserData()?.contributionAmount || 0,
    nextBillingDate: getInitialUserData()?.nextBillingDate || null
  },
  messages: {
    userMessages: [],
    adminMessages: [],
    sentMessages: [], // Initialize sentMessages state
    totalSentMessages: 0, // Total count of sent messages
    unreadUserMessages: 0,
    unreadAdminMessages: 0,
    loading: false,
    error: null,
    sendingMessage: false,
    sendError: null,
    markingRead: false,
    markReadError: null,
    sentMessagesLoading: false, // Loading state for sent messages
    sentMessagesError: null // Error state for sent messages
  },
  avatarSelector: {
    loading: false,
    error: null,
    tiers: [],
    unlockedTiers: [],
    currentAvatar: getInitialUserData()?.userAvatar || ""
  },
  // Friends list state ("Kin" — close friends / chosen connections)
  friendsList: {
    items: [],
    loading: false,
    error: null,
    addFriendLoading: false,
    addFriendError: null,
    removeFriendLoading: false,
    removeFriendError: null
  },
  // Members list state (all community members)
  membersList: {
    items: [],
    loading: false,
    error: null,
    totalResults: 0,
    currentPage: 1,
    totalPages: 1,
    hasMore: false
  },
  // Add separate state for MU allotment functionality
  allotMU: {
    loading: false,
    error: null,
    lastTransaction: null
  }
};

export const authReducer = (state = initialState, action) => {
  const { type, payload } = action;

  switch (type) {
    case LOGIN_TYPES.LOGIN:
      return {
        ...state,
        isAuth: true,
        email: payload.email,
        password: payload.password,
        token: payload.token,
        loginAttempts: 0,
        isAccountLocked: false,
        lockUntil: null,
        user: payload.user,
        isAdmin: payload.isAdmin || (payload.user?.role === 'admin' || payload.user?.accountType === 'admin'),
        adminLevel: payload.user?.adminLevel || null,
        hasAdminAccess: payload.user?.hasAdminAccess || payload.isAdmin || false,
        // Update profile data from login response
        profile: {
          userBio: payload.user?.userBio || "",
          accountCredits: payload.user?.accountCredits || 0,
          burrowPoints: payload.user?.burrowPoints || 5,
          accountLevel: payload.user?.accountLevel || 1,
          accountType: payload.user?.accountType || "free",
          userAvatar: payload.user?.userAvatar || "",
          membershipTier: payload.user?.membershipTier || state.profile.membershipTier || "base",
          userState: payload.user?.userState || state.profile.userState || null
        }
      };

    case LOGIN_TYPES.VALIDITY_TOKEN:
      return {
        ...state,
        isAuth: payload,
      };

    case LOGIN_TYPES.LOGOUT:
      return {
        ...initialState,
        isAuth: false,
        token: "",
        user: null,
        isAdmin: false,
        adminLevel: null,
        hasAdminAccess: false,
        isInAdminMode: false,
        adminToken: null,
        // Make sure all user-related fields are cleared
        email: "",
        password: "",
        loginAttempts: 0,
        isAccountLocked: false,
        lockUntil: null,
        // Clear profile data too
        profile: {
          userBio: "",
          accountCredits: 0,
          burrowPoints: 5,
          accountLevel: 1,
          accountType: "free",
          userAvatar: "" // Clear userAvatar
        }
      };
      
    case LOGIN_TYPES.LOGIN_ATTEMPT_FAILED:
      return {
        ...state,
        loginAttempts: payload.attempts,
      };
      
    case LOGIN_TYPES.LOGIN_ATTEMPT_RESET:
      return {
        ...state,
        loginAttempts: 0,
        isAccountLocked: false,
        lockUntil: null,
      };
      
    case LOGIN_TYPES.LOGIN_ACCOUNT_LOCKED:
      return {
        ...state,
        loginAttempts: payload.attempts,
        isAccountLocked: false, // Changed from true to false to prevent locking
        lockUntil: null, // Set to null instead of payload.until
      };

    case LOGIN_TYPES.SET_USER_DATA:
      return {
        ...state,
        user: payload.user,
        isAdmin: payload.isAdmin || false,
        // Update profile from user data if available
        profile: {
          ...state.profile,
          userBio: payload.user?.userBio || state.profile.userBio,
          accountCredits: payload.user?.accountCredits || state.profile.accountCredits,
          burrowPoints: payload.user?.burrowPoints || state.profile.burrowPoints,
          accountLevel: payload.user?.accountLevel || state.profile.accountLevel,
          accountType: payload.user?.accountType || state.profile.accountType,
          userAvatar: payload.user?.userAvatar || state.profile.userAvatar // Update userAvatar
        }
      };

    case LOGIN_TYPES.REFRESH_AUTH_STATE:
      // Re-read from StorageManager to ensure state is in sync
      const refreshedUser = StorageManager.get('burrowUserData', null);

      return {
        ...state,
        isAuth: Boolean(StorageManager.get('burrowtoken')),
        token: StorageManager.get('burrowtoken', ''),
        user: refreshedUser,
        isAdmin: StorageManager.get('isAdmin', false), // Refresh isAdmin from localStorage
        // Refresh profile data if available
        profile: refreshedUser ? {
          userBio: refreshedUser.userBio || state.profile.userBio,
          accountCredits: refreshedUser.accountCredits || state.profile.accountCredits,
          burrowPoints: refreshedUser.burrowPoints || state.profile.burrowPoints,
          accountLevel: refreshedUser.accountLevel || state.profile.accountLevel,
          accountType: refreshedUser.accountType || state.profile.accountType,
          userAvatar: refreshedUser.userAvatar || state.profile.userAvatar // Refresh userAvatar
        } : state.profile
      };

    // New profile action handlers
    case LOGIN_TYPES.FETCH_PROFILE_REQUEST:
      return {
        ...state,
        profileLoading: true,
        profileError: null
      };

    case LOGIN_TYPES.FETCH_PROFILE_SUCCESS:
      return {
        ...state,
        profileLoading: false,
        profile: {
          ...state.profile,
          ...payload.profile
        },
        user: {
          ...state.user,
          ...payload.profile
        }
      };

    case LOGIN_TYPES.FETCH_PROFILE_FAILURE:
      return {
        ...state,
        profileLoading: false,
        profileError: payload.error
      };

    case LOGIN_TYPES.UPDATE_BIO_REQUEST:
      return {
        ...state,
        profileLoading: true,
        profileError: null
      };

    case LOGIN_TYPES.UPDATE_BIO_SUCCESS:
      return {
        ...state,
        profileLoading: false,
        profile: {
          ...state.profile,
          userBio: payload.bio
        },
        user: {
          ...state.user,
          userBio: payload.bio
        }
      };

    case LOGIN_TYPES.UPDATE_BIO_FAILURE:
      return {
        ...state,
        profileLoading: false,
        profileError: payload.error
      };

    case LOGIN_TYPES.UPDATE_STATE_REQUEST:
      return {
        ...state,
        profileLoading: true,
        profileError: null
      };

    case LOGIN_TYPES.UPDATE_STATE_SUCCESS:
      return {
        ...state,
        profileLoading: false,
        profile: {
          ...state.profile,
          userState: payload.userState
        },
        user: {
          ...state.user,
          userState: payload.userState
        }
      };

    case LOGIN_TYPES.UPDATE_STATE_FAILURE:
      return {
        ...state,
        profileLoading: false,
        profileError: payload.error
      };

    case LOGIN_TYPES.VERIFY_ORDER_REQUEST:
      return {
        ...state,
        profileLoading: true,
        profileError: null
      };

    case LOGIN_TYPES.VERIFY_ORDER_SUCCESS:
      return {
        ...state,
        profileLoading: false,
        profile: {
          ...state.profile,
          accountCredits: payload.credits
        },
        user: {
          ...state.user,
          accountCredits: payload.credits
        }
      };

    case LOGIN_TYPES.VERIFY_ORDER_FAILURE:
      return {
        ...state,
        profileLoading: false,
        profileError: payload.error
      };

    case LOGIN_TYPES.REPORT_ISSUE_REQUEST:
      return {
        ...state,
        profileLoading: true,
        profileError: null
      };

    case LOGIN_TYPES.REPORT_ISSUE_SUCCESS:
      return {
        ...state,
        profileLoading: false
      };

    case LOGIN_TYPES.REPORT_ISSUE_FAILURE:
      return {
        ...state,
        profileLoading: false,
        profileError: payload.error
      };

    case LOGIN_TYPES.FETCH_MESSAGES_REQUEST:
      return {
        ...state,
        messages: {
          ...state.messages,
          loading: true,
          error: null
        }
      };

    case LOGIN_TYPES.FETCH_MESSAGES_SUCCESS:
      return {
        ...state,
        messages: {
          ...state.messages,
          userMessages: payload.userMessages || [],
          adminMessages: payload.adminMessages || [],
          unreadUserMessages: payload.unreadUserMessages || 0,
          unreadAdminMessages: payload.unreadAdminMessages || 0,
          loading: false,
          error: null
        }
      };

    case LOGIN_TYPES.FETCH_MESSAGES_FAILURE:
      return {
        ...state,
        messages: {
          ...state.messages,
          loading: false,
          error: payload.error
        }
      };

    case LOGIN_TYPES.SEND_MESSAGE_REQUEST:
      return {
        ...state,
        messages: {
          ...state.messages,
          sendingMessage: true,
          sendError: null
        }
      };

    case LOGIN_TYPES.SEND_MESSAGE_SUCCESS:
      return {
        ...state,
        messages: {
          ...state.messages,
          sendingMessage: false,
          sendError: null
        }
      };

    case LOGIN_TYPES.SEND_MESSAGE_FAILURE:
      return {
        ...state,
        messages: {
          ...state.messages,
          sendingMessage: false,
          sendError: payload.error
        }
      };

    case LOGIN_TYPES.MARK_MESSAGE_READ_REQUEST:
      return {
        ...state,
        messages: {
          ...state.messages,
          markingRead: true,
          markReadError: null
        }
      };

    case LOGIN_TYPES.MARK_MESSAGE_READ_SUCCESS:
      return {
        ...state,
        messages: {
          ...state.messages,
          markingRead: false,
          markReadError: null,
          userMessages: payload.messageType === 'user' 
            ? state.messages.userMessages.map(msg => 
                payload.messageIds.includes(msg.userMessageId) 
                  ? {...msg, isRead: true} 
                  : msg
              )
            : state.messages.userMessages,
          adminMessages: payload.messageType === 'admin' 
            ? state.messages.adminMessages.map(msg => 
                payload.messageIds.includes(msg.adminMessageId) 
                  ? {...msg, isRead: true} 
                  : msg
              )
            : state.messages.adminMessages,
          unreadUserMessages: payload.messageType === 'user' 
            ? Math.max(0, state.messages.unreadUserMessages - payload.updateCount) 
            : state.messages.unreadUserMessages,
          unreadAdminMessages: payload.messageType === 'admin' 
            ? Math.max(0, state.messages.unreadAdminMessages - payload.updateCount) 
            : state.messages.unreadAdminMessages
        }
      };

    case LOGIN_TYPES.MARK_MESSAGE_READ_FAILURE:
      return {
        ...state,
        messages: {
          ...state.messages,
          markingRead: false,
          markReadError: payload.error
        }
      };
      
    case LOGIN_TYPES.FETCH_SENT_MESSAGES_REQUEST:
      return {
        ...state,
        messages: {
          ...state.messages,
          sentMessagesLoading: true,
          sentMessagesError: null
        }
      };

    case LOGIN_TYPES.FETCH_SENT_MESSAGES_SUCCESS:
      return {
        ...state,
        messages: {
          ...state.messages,
          sentMessages: payload.messages || [],
          totalSentMessages: payload.total || 0,
          sentMessagesLoading: false,
          sentMessagesError: null
        }
      };

    case LOGIN_TYPES.FETCH_SENT_MESSAGES_FAILURE:
      return {
        ...state,
        messages: {
          ...state.messages,
          sentMessagesLoading: false,
          sentMessagesError: payload.error
        }
      };
      
    case LOGIN_TYPES.FETCH_AVATARS_REQUEST:
      return {
        ...state,
        avatarSelector: {
          ...state.avatarSelector,
          loading: true,
          error: null
        }
      };
      
    case LOGIN_TYPES.FETCH_AVATARS_SUCCESS:
      return {
        ...state,
        avatarSelector: {
          ...state.avatarSelector,
          loading: false,
          error: null,
          tiers: action.payload.tiers,
          unlockedTiers: action.payload.unlockedTiers,
          currentAvatar: action.payload.currentAvatar
        }
      };
      
    case LOGIN_TYPES.FETCH_AVATARS_FAILURE:
      return {
        ...state,
        avatarSelector: {
          ...state.avatarSelector,
          loading: false,
          error: action.payload
        }
      };
      
    case LOGIN_TYPES.UPDATE_AVATAR_REQUEST:
      return {
        ...state,
        profileLoading: true,
        profileError: null
      };
      
    case LOGIN_TYPES.UPDATE_AVATAR_SUCCESS:
      return {
        ...state,
        profileLoading: false,
        profileError: null,
        profile: {
          ...state.profile,
          userAvatar: action.payload.userAvatar
        },
        avatarSelector: {
          ...state.avatarSelector,
          currentAvatar: action.payload.userAvatar
        }
      };
      
    case LOGIN_TYPES.UPDATE_AVATAR_FAILURE:
      return {
        ...state,
        profileLoading: false,
        profileError: action.payload
      };
      
    // Friends list cases
    case LOGIN_TYPES.FETCH_FRIENDS_LIST_REQUEST:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          loading: true,
          error: null
        }
      };
      
    case LOGIN_TYPES.FETCH_FRIENDS_LIST_SUCCESS:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          items: payload.friendsList || [],
          loading: false,
          error: null
        }
      };
      
    case LOGIN_TYPES.FETCH_FRIENDS_LIST_FAILURE:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          loading: false,
          error: payload.error
        }
      };
      
    case LOGIN_TYPES.ADD_FRIEND_REQUEST:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          addFriendLoading: true,
          addFriendError: null
        }
      };
      
    case LOGIN_TYPES.ADD_FRIEND_SUCCESS:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          items: payload.friendsList || [],
          addFriendLoading: false,
          addFriendError: null
        }
      };
      
    case LOGIN_TYPES.ADD_FRIEND_FAILURE:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          addFriendLoading: false,
          addFriendError: payload.error
        }
      };
      
    case LOGIN_TYPES.REMOVE_FRIEND_REQUEST:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          removeFriendLoading: true,
          removeFriendError: null
        }
      };
      
    case LOGIN_TYPES.REMOVE_FRIEND_SUCCESS:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          items: payload.friendsList || [],
          removeFriendLoading: false,
          removeFriendError: null
        }
      };
      
    case LOGIN_TYPES.REMOVE_FRIEND_FAILURE:
      return {
        ...state,
        friendsList: {
          ...state.friendsList,
          removeFriendLoading: false,
          removeFriendError: payload.error
        }
      };

    // Members list cases
    case LOGIN_TYPES.FETCH_MEMBERS_REQUEST:
      return {
        ...state,
        membersList: { ...state.membersList, loading: true, error: null }
      };
    case LOGIN_TYPES.FETCH_MEMBERS_SUCCESS:
      return {
        ...state,
        membersList: {
          ...state.membersList,
          items: payload.members || [],
          totalResults: payload.totalResults || 0,
          currentPage: payload.currentPage || 1,
          totalPages: payload.totalPages || 1,
          hasMore: payload.hasMore || false,
          loading: false,
          error: null
        }
      };
    case LOGIN_TYPES.FETCH_MEMBERS_FAILURE:
      return {
        ...state,
        membersList: {
          ...state.membersList,
          loading: false,
          error: payload.error
        }
      };

    // Allot Member Units cases
    case LOGIN_TYPES.ALLOT_MU_REQUEST:
      return { 
        ...state, 
        allotMU: {
          ...state.allotMU,
          loading: true, 
          error: null
        }
      };
      
    case LOGIN_TYPES.ALLOT_MU_SUCCESS:
      return { 
        ...state, 
        profile: {
          ...state.profile,
          accountCredits: payload.newBalance
        },
        allotMU: {
          loading: false,
          error: null,
          lastTransaction: payload.transaction
        }
      };
      
    case LOGIN_TYPES.ALLOT_MU_FAILURE:
      return { 
        ...state, 
        allotMU: {
          ...state.allotMU,
          loading: false, 
          error: payload.error
        }
      };

    // Phase 1C: Admin elevation — user enters admin mode with admin JWT
    case LOGIN_TYPES.ADMIN_ELEVATE_SUCCESS:
      return {
        ...state,
        isInAdminMode: true,
        isAdmin: true,
        adminToken: payload.adminToken,
        adminLevel: payload.admin?.adminLevel || 'admin'
      };

    // Phase 1C: Switch back from admin mode to user mode
    case LOGIN_TYPES.ADMIN_SWITCH_TO_USER:
      return {
        ...state,
        isInAdminMode: false,
        isAdmin: false,
        adminToken: null,
        adminLevel: null,
        // If a new user token was issued, update it
        ...(payload.token ? { token: payload.token } : {}),
        ...(payload.user ? { user: payload.user } : {})
      };

    default:
      return state;
  }
};

// Export loginreducer as an alias of authReducer for backward compatibility
export const loginreducer = authReducer;